import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { colors } from '../../styles/tokens'
import { Button, Input } from '../../components/FormControls'

// Package F — Category Management. Organizational metadata only: this
// component never touches measurement, geometry, calibration, or
// aggregation logic. Categories exist purely to group Takeoff Items
// for reporting purposes.
export default function CategoryManagement({ tenderId, onClose }) {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [newName, setNewName] = useState('')
  const [newCode, setNewCode] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editCode, setEditCode] = useState('')

  async function load() {
    setLoading(true)
    const { data, error: err } = await supabase
      .from('takeoff_category')
      .select('id, name, code, sort_order, is_active')
      .eq('tender_id', tenderId)
      .order('sort_order')
    if (err) { setError(err.message); setLoading(false); return }
    // Item counts fetched separately — a plain count-per-category
    // query, not a new aggregation engine. This never touches
    // measurement/quantity data, only counts existing takeoff_item rows.
    const catIds = (data || []).map((c) => c.id)
    let countsById = new Map()
    if (catIds.length > 0) {
      const { data: items } = await supabase.from('takeoff_item').select('takeoff_category_id').in('takeoff_category_id', catIds)
      countsById = new Map()
      for (const it of items || []) countsById.set(it.takeoff_category_id, (countsById.get(it.takeoff_category_id) || 0) + 1)
    }
    setCategories((data || []).map((c) => ({ ...c, item_count: countsById.get(c.id) || 0 })))
    setLoading(false)
  }

  useEffect(() => { load() }, [tenderId])

  async function handleCreate(e) {
    e.preventDefault()
    if (!newName.trim()) return
    const nextOrder = categories.length > 0 ? Math.max(...categories.map((c) => c.sort_order)) + 1 : 0
    const { error: err } = await supabase.from('takeoff_category').insert({
      tender_id: tenderId, name: newName.trim(), code: newCode.trim() || null, sort_order: nextOrder,
    })
    if (err) { setError(err.message); return }
    setNewName(''); setNewCode('')
    await load()
  }

  function startEdit(cat) {
    setEditingId(cat.id); setEditName(cat.name); setEditCode(cat.code || '')
  }

  async function saveEdit(id) {
    if (!editName.trim()) return
    const { error: err } = await supabase.from('takeoff_category').update({ name: editName.trim(), code: editCode.trim() || null }).eq('id', id)
    if (err) { setError(err.message); return }
    setEditingId(null)
    await load()
  }

  // Reorder via simple up/down swap — no drag-and-drop complexity
  // needed for a small, infrequently-reordered list. Swaps sort_order
  // values between adjacent rows only.
  async function move(index, direction) {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= categories.length) return
    const a = categories[index], b = categories[targetIndex]
    const { error: err1 } = await supabase.from('takeoff_category').update({ sort_order: b.sort_order }).eq('id', a.id)
    const { error: err2 } = await supabase.from('takeoff_category').update({ sort_order: a.sort_order }).eq('id', b.id)
    if (err1 || err2) { setError((err1 || err2).message); return }
    await load()
  }

  async function toggleActive(cat) {
    const { error: err } = await supabase.from('takeoff_category').update({ is_active: !cat.is_active }).eq('id', cat.id)
    if (err) { setError(err.message); return }
    await load()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 8, width: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>Manage Categories</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: colors.textMuted }}>×</button>
        </div>

        {error && <div style={{ padding: '8px 18px', color: colors.danger, fontSize: 12 }}>{error}</div>}

        <div style={{ overflowY: 'auto', flex: 1, padding: '10px 18px' }}>
          {loading ? (
            <div style={{ color: colors.textMuted, fontSize: 13 }}>Loading...</div>
          ) : categories.length === 0 ? (
            <div style={{ color: colors.textMuted, fontSize: 13 }}>No categories yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.border}`, textAlign: 'left' }}>
                  <th style={{ padding: '6px 4px', width: 30 }}></th>
                  <th style={{ padding: '6px 4px', width: 70 }}>Code</th>
                  <th style={{ padding: '6px 4px' }}>Category</th>
                  <th style={{ padding: '6px 4px', width: 60, textAlign: 'right' }}>Items</th>
                  <th style={{ padding: '6px 4px', width: 60 }}>Status</th>
                  <th style={{ padding: '6px 4px', width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat, idx) => (
                  <tr key={cat.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '6px 4px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <button onClick={() => move(idx, -1)} disabled={idx === 0} style={{ border: 'none', background: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? colors.border : colors.textMuted, fontSize: 10, lineHeight: 1 }}>▲</button>
                        <button onClick={() => move(idx, 1)} disabled={idx === categories.length - 1} style={{ border: 'none', background: 'none', cursor: idx === categories.length - 1 ? 'default' : 'pointer', color: idx === categories.length - 1 ? colors.border : colors.textMuted, fontSize: 10, lineHeight: 1 }}>▼</button>
                      </div>
                    </td>
                    {editingId === cat.id ? (
                      <>
                        <td style={{ padding: '4px' }}><Input value={editCode} onChange={(e) => setEditCode(e.target.value)} style={{ fontSize: 12, padding: '3px 6px', width: 60 }} /></td>
                        <td style={{ padding: '4px' }}><Input value={editName} onChange={(e) => setEditName(e.target.value)} style={{ fontSize: 12, padding: '3px 6px' }} /></td>
                        <td colSpan={2}></td>
                        <td style={{ padding: '4px', whiteSpace: 'nowrap' }}>
                          <button onClick={() => saveEdit(cat.id)} style={{ fontSize: 11, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Save</button>
                          <button onClick={() => setEditingId(null)} style={{ fontSize: 11, color: colors.textMuted, background: 'none', border: 'none', cursor: 'pointer', marginLeft: 6 }}>Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '6px 4px', color: colors.textMuted }}>{cat.code || '-'}</td>
                        <td style={{ padding: '6px 4px', color: cat.is_active ? colors.textPrimary : colors.textMuted, fontWeight: 600 }}>{cat.name}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'right', color: colors.textMuted }}>{cat.item_count ?? '-'}</td>
                        <td style={{ padding: '6px 4px' }}>
                          <button onClick={() => toggleActive(cat)} style={{ fontSize: 10, fontWeight: 600, color: cat.is_active ? colors.accentGreen : colors.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}>
                            {cat.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td style={{ padding: '6px 4px' }}>
                          <button onClick={() => startEdit(cat)} style={{ fontSize: 11, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <form onSubmit={handleCreate} style={{ display: 'flex', gap: 6, padding: '10px 18px', borderTop: `1px solid ${colors.border}` }}>
          <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="Code" style={{ width: 70, fontSize: 12 }} />
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New category name" style={{ flex: 1, fontSize: 12 }} required />
          <Button type="submit" size="sm">Add</Button>
        </form>
      </div>
    </div>
  )
}
